/* istanbul ignore file */
import { differenceInDays, format, parse, startOfDay } from 'date-fns';
import { HAVEN_HOLIDAY_PRESENTABLE, HIDEAWAY, NOT_SELECTED } from '../../constant';
import { Query } from '../../types/v2/holiday-search';

import { analytics } from './analytics';
import {
  getPartyConfig,
  getSearchType,
  stringifyAndEscapeQuotes,
  getProductsData,
  getNoResultsReason,
} from './gtm-shared';
import { AggregatedAccommodationType } from '../../module/aggregating-by-grade/type/types';
import { getCurrentCartElementFromLocalStorage } from '..';
import { getHolidayPackageType } from '../util';
import { AggregatingResults } from '../../types/aggregating';
import { SmartseerResponseMetadata } from '../../types/smartseer';

export const getSearchDetails = (
  query: Query,
  facets: Record<string, any>[],
  pageName: string,
  aggregationType: 'grade' | 'room' | 'package' | 'date' | 'park',
  SmartseerResponseMetadata: SmartseerResponseMetadata | null,
  interactionProps?: string,
  pageHasResults = true,
  alertsBarMessage: string | null = null,
) => {
  const monthsDates =
    query?.month && !query?.arrivalDate
      ? facets
          .find((x) => x.facetName === 'arrivalDate')
          ?.items?.filter((x: any) => x.key)
          ?.map((x: any) => format(parse(x?.key, 'dd/MM/yyyy', new Date()), 'yyyy-MM-dd'))
          .sort()
      : null;

  const monthsDuration =
    query?.month && !query?.arrivalDate && !query?.duration
      ? facets
          .find((x) => x.facetName === 'duration')
          ?.items?.filter((x: any) => x.raw_key)
          ?.map(({ raw_key }: any) => Number(raw_key))
          .sort((a: number, b: number) => a - b)
          .join('|')
      : null;

  const grades =
    aggregationType === 'grade'
      ? facets.flatMap((type) =>
          type.subItems.map((subType: any) => subType.accommodationSubType?.toLowerCase()),
        )
      : [];

  const parkCodes = query?.parkCode?.split(',')?.sort((a: any, b: any) => (a < b ? -1 : 1));

  const arrivalDates = monthsDates || (query?.arrivalDate || '').split(',').sort();

  const activeBooking = getCurrentCartElementFromLocalStorage();
  const { products: activeBookingProduct } = getProductsData([], true, activeBooking);

  const shouldShowSsGroups =
    SmartseerResponseMetadata && SmartseerResponseMetadata?.isInTestGroup !== false;

  const daysToArrival =
    arrivalDates?.length && arrivalDates[0].length
      ? differenceInDays(parse(arrivalDates[0], 'yyyy-MM-dd', new Date()), startOfDay(new Date()))
      : undefined;

  const updatedInteractionProps = [
    ...(interactionProps ? interactionProps.split(',') : []),
    ...(daysToArrival !== undefined ? [`daystoarrival-${daysToArrival}`] : []),
    ...[`pageHasResults-${pageHasResults}`],
  ].join(',');

  return {
    event: 'virtual page view',
    productType: 'self catering',
    pageType: 'holidays',
    pageSubType: 'search',
    pageName: pageName || '',
    pageId: '(no id)',
    parkCode: parkCodes?.join('|') || NOT_SELECTED,
    arrivalDate: arrivalDates?.join('|') || NOT_SELECTED,
    daysToArrival: daysToArrival !== undefined ? daysToArrival : undefined,
    interactionProps: updatedInteractionProps,
    duration:
      monthsDuration ||
      (query?.duration || '')
        .split(',')
        ?.map((dur: any) => Number(dur))
        .sort((a: number, b: number) => a - b)
        .join('|') ||
      NOT_SELECTED,
    adults: query?.adults !== null ? Number(query?.adults) : NOT_SELECTED,
    children: query?.children !== null ? Number(query?.children) : NOT_SELECTED,
    infants: query?.tots !== null ? Number(query?.tots) : NOT_SELECTED,
    dogs: query?.dogs !== null ? Number(query?.dogs) : NOT_SELECTED,
    accessible: query?.accessibleVans === 'true' ? 'true' : 'false',
    numParks: parkCodes?.length || 0,
    ...(aggregationType === 'grade' && { accomGradeName: grades.sort().join('|') }),
    numArrivalDates: arrivalDates?.length || 0,
    numGuests: +(query?.adults || 0) + +(query?.children || 0) + +(query?.tots || 0),
    numUnderSixteens: +(query?.children || 0) + +(query?.tots || 0),
    partyConfig: getPartyConfig(query),
    searchType: getSearchType(query),
    packageType: getPackageTypeJourney(query?.packageType),
    accomBedrooms: getBedroomCountJourney(query?.selectedBeds),
    accomGradeCode: getGradeCodeJourney(query?.selectedGradeCode),
    accomGradeName: getGradeNameJourney(query?.selectedGradeName),
    sortBy: getSortOrder(aggregationType, facets),
    products: activeBookingProduct,
    searchResultsFlag: pageHasResults ? 'results' : 'no results',
    errorType: !pageHasResults ? 'err_search_no_results' : null,
    errorReason: !pageHasResults ? getNoResultsReason(query, aggregationType) : null,
    alertsBarMessage,
    ...(shouldShowSsGroups && {
      ss_groups: stringifyAndEscapeQuotes(SmartseerResponseMetadata),
    }),
  };
};

export const setPageData = (
  query: Query,
  searchResults: AggregatedAccommodationType[] | AggregatingResults[],
  pageName: string,
  aggregationType: 'grade' | 'room' | 'package' | 'date' | 'park',
  SmartseerResponseMetadata: SmartseerResponseMetadata | null = null,
  interactionProps?: string,
  hasResults = true,
  alertsBarMessage?: string | null,
) => {
  const searchDetails = getSearchDetails(
    query,
    searchResults,
    pageName,
    aggregationType,
    SmartseerResponseMetadata,
    interactionProps,
    hasResults,
    alertsBarMessage,
  );

  analytics({
    ...searchDetails,
  });
};

const getPackageTypeJourney = (packageType: string | undefined) => {
  if (!packageType) return null;

  const formattedPackage = getHolidayPackageType(packageType);
  if (formattedPackage === HIDEAWAY) return HIDEAWAY;
  if (formattedPackage === HAVEN_HOLIDAY_PRESENTABLE) return HAVEN_HOLIDAY_PRESENTABLE;

  return null;
};

const getBedroomCountJourney = (selectedBeds: string | undefined) => {
  if (!selectedBeds) return null;

  return String(selectedBeds);
};

const getGradeCodeJourney = (gradeCode: string | undefined) => {
  if (!gradeCode) return null;

  return gradeCode;
};

const getGradeNameJourney = (gradeName: string | undefined) => {
  if (!gradeName) return null;

  return gradeName.toLowerCase();
};

export const getSortOrder = (
  aggregationType: string | undefined,
  facets: Record<string, any>[],
) => {
  if (aggregationType === 'grade') {
    return facets
      ?.map((accommodationType) => accommodationType?.accommodationType?.toLowerCase())
      ?.join('|');
  }
};

export const handleCardWithOccupancyIndicator = (
  gradeName: string,
  totalOccupancy: number,
  totalUnitsRemaining: number,
  label: string,
) =>
  analytics(
    {
      event: 'experiment interaction',
      interactionName: 'accommodation card with occupancy indicator click',
      interactionProps: `grade-${gradeName}|occupancy-${totalOccupancy?.toFixed(
        2,
      )}|units-${totalUnitsRemaining}|label-${label.toLocaleLowerCase()}`,
    },

    false,
  );

export const handleRoomCardWithOccupancyIndicator = (
  beds: string,
  totalOccupancy: number,
  totalUnitsRemaining: number,
  label: string,
) =>
  analytics(
    {
      event: 'experiment interaction',
      interactionName: 'room card with occupancy indicator click',
      interactionProps: `beds-${beds}|occupancy-${totalOccupancy?.toFixed(
        2,
      )}|units-${totalUnitsRemaining}|label-${label.toLocaleLowerCase()}`,
    },

    false,
  );

export const handleSelectItemPartnerPricing = (
  itemType: string,
  affiliatePromoCode: string,
  price: number,
  discountedPrice?: number,
) => {
  if (affiliatePromoCode === 'EMB_BLUELIGHT') {
    const discountedPriceString = discountedPrice ? `|discountedPrice:${discountedPrice}` : '';
    const discountString = `|discount:${discountedPrice ? price - discountedPrice : 0}`;

    analytics({
      event: 'experiment interaction',
      interactionName: `select ${itemType} click`,
      interactionProps: `price:${price}${discountedPriceString}${discountString}`,
    });
  }
};

export const handleStepperNavigationOpen = (activeStep: string) =>
  analytics(
    {
      event: 'experiment interaction',
      interactionName: 'open stepper navigation',
      interactionProps: `active-step-${activeStep}`,
    },
    false,
  );

export const handleStepperNavigation = (fromStep: string, toStep: string) =>
  analytics(
    {
      event: 'experiment interaction',
      interactionName: 'stepper navigation click',
      interactionProps: `from-step-${fromStep}|to-step-${toStep}`,
    },
    false,
  );

export const handleStepperLogin = (fromStep: string) =>
  analytics(
    {
      event: 'experiment interaction',
      interactionName: 'stepper login button click',
      interactionProps: `from-step-${fromStep}`,
    },
    false,
  );

export const handleNoResultsNavigation = (fromStep: string, toStep: string) =>
  analytics(
    {
      event: 'experiment interaction',
      interactionName: 'no results navigation click',
      interactionProps: `from-step-${fromStep}|to-step-${toStep}`,
    },
    false,
  );

export const handleSkipPDPPage = () => {
  analytics(
    {
      event: 'experiment interaction',
      interactionName: 'skip PDP page and redirect to checkout',
    },
    false,
  );
};

export const handleToastNotification = (
  clickedDate: string | null,
  numberOfNights: number | null,
) => {
  const dayDate = clickedDate ? parse(clickedDate, 'yyyy-MM-dd', new Date()) : null;
  const dayName = dayDate ? format(dayDate, 'eeee') : '';

  return analytics(
    {
      event: 'visitor interaction',
      interactionName: 'alert panel',
      alertType: 'calendar notification: find alternate arrival date',
      calendarArrivalDay: dayName?.toLowerCase(),
      duration: numberOfNights,
      calendarArrivalDate: clickedDate,
    },
    false,
  );
};

export const handleGoogleRatingsClick = (parkCode: string) =>
  analytics(
    {
      event: 'experiment interaction',
      interactionName: 'hol pdp google ratings click',
      interactionProps: `park-code-${parkCode}`,
    },
    false,
  );

export const handleBookNowButtonClickWithOccupancyIndicator = (
  totalOccupancy: number,
  totalUnitsRemaining: number,
  label: string,
  id: string,
) =>
  analytics(
    {
      event: 'experiment interaction',
      interactionName: 'book now button with occupancy indicator click',
      interactionProps: `occupancy-${totalOccupancy?.toFixed(
        2,
      )}id-${id}|units-${totalUnitsRemaining}|label-${label.toLocaleLowerCase()}`,
    },

    false,
  );
